import useWebSocketWithToken from "./use-websocket-with-token";
import {WS_BASE_URL} from "../constants/api-url";
import {HOME, SUMMARY} from "../constants/routes";
import {useNavigate} from "react-router-dom";
import {useCallback, useContext} from "react";
import exchangeWsToken from "../services/ws-token";
import {switchCase} from "../utils/helper";
import {CameraEventEnum} from "../models/enums/camera-event.enum";
import {MessageDTO} from "../models/dto/message-dto";
import fetchContext from "../services/context.ts";
import {StatusEnum} from "../models/enums/status.enum.ts";
import {AppStoreContext} from "../store/app-store.ts";
import {initApp} from "../routes/initialise-application.ts";

const useWebSocket = () => {
    const navigate = useNavigate();
    const { setReport, setContext, setStatus, setInitState, setHasAppointment } = useContext(AppStoreContext);

    const refreshState = useCallback(async () => {
        const refreshedState = await initApp();
        setInitState(refreshedState);
    }, [setInitState]);

    const onUnpair = useCallback(() => {
        refreshState();
    }, [refreshState]);

    const onPair = useCallback((data: MessageDTO) => {
        localStorage.setItem("jwt", data.token);
        setContext({cameraStatus: data.status, tvName: data.name});
        setStatus(StatusEnum.PAIRED);
    }, [setContext, setStatus]);

    const onReport = useCallback((data: MessageDTO) => {
        try {
            setHasAppointment(data.hasAppointment);
            setReport(data.report);
            navigate(SUMMARY);
        } catch (error) {
            console.error(error);
        }
    }, [navigate, setReport, setHasAppointment]);

    const onLeave = useCallback(() => {
        setHasAppointment(undefined);
        navigate(`/${HOME}`);
    }, [navigate, setHasAppointment]);

    const onStatusChange = useCallback((message: MessageDTO) => {
        setContext({cameraStatus: message.status})
    }, [setContext]);

    const onNameChange = useCallback((message: MessageDTO) => {
        setContext({tvName: message.name})
    }, [setContext]);

    const onBackgroundUpdate = useCallback(async () => {
        setTimeout(async () => {
            const updatedCtx = await fetchContext();
            setContext({ backgroundMedia: updatedCtx.backgroundMedia });
        }, 10000);
    }, [setContext]);

    const onDisplayModeUpdate = useCallback(async () => {
        const updatedCtx = await fetchContext();
        setContext({
            displayMode: updatedCtx.displayMode,
            withAppointmentMedia: updatedCtx.withAppointmentMedia,
            withoutAppointmentMedia: updatedCtx.withoutAppointmentMedia,
        });
    }, [setContext]);

    const onMessage = useCallback((message: MessageDTO) => {
        switchCase({
            [CameraEventEnum.CAMERA_STATUS_CHANGE]: () => onStatusChange(message),
            [CameraEventEnum.CAMERA_NAME_CHANGE]: () => onNameChange(message),
            [CameraEventEnum.VEHICLE_INTEGRATION]: () => onReport(message),
            [CameraEventEnum.VEHICLE_LEAVE]: onLeave,
            [CameraEventEnum.CAMERA_UNPAIR]: onUnpair,
            [CameraEventEnum.CAMERA_PAIR]: () => onPair(message),
            [CameraEventEnum.TV_BACKGROUND_UPDATE]: onBackgroundUpdate,
            [CameraEventEnum.TV_DISPLAY_MODE_UPDATE]: onDisplayModeUpdate,
        })(message.event)();
    }, [onLeave, onReport, onPair, onUnpair, onStatusChange, onNameChange, onBackgroundUpdate, onDisplayModeUpdate]);

    const { closeConnection } = useWebSocketWithToken(
        WS_BASE_URL!,
        exchangeWsToken,
        {
            onError: (error) => {
                console.error('WebSocket Error:', error)
            },
            onMessage,
            reconnectInterval: 10000,
        }
    );

    window.addEventListener("beforeunload", () => {
        closeConnection();
    });

    return { closeConnection };
}

export default useWebSocket;
